import { AlertTriangle, CheckCircle2, Circle, Clock, FileSpreadsheet, FileText, RotateCcw, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { ApiError, apiOuvrir, apiTelecharger } from '@/api/client';
import { LIBELLE_DECISION, LIBELLE_ETAPE, LIBELLE_PAIEMENT, LIBELLE_STATUT, type Circuit, type Controle, type DecisionApi, type Etape, type EtatPaiement, type StatutDocument } from '@/api/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateHeureFr } from '@/lib/utils';

// ------------------------------------------------------------------ badges
const COULEUR_STATUT: Record<StatutDocument, string> = {
  BROUILLON: 'bg-muted text-muted-foreground',
  SOUMIS: 'bg-blue-100 text-blue-800',
  A_CORRIGER: 'bg-amber-100 text-amber-900',
  AUTORISE: 'bg-emerald-100 text-emerald-800',
  REJETE: 'bg-red-100 text-red-800',
  ANNULE: 'bg-neutral-200 text-neutral-700',
};

export const StatutBadge = ({ statut }: { statut: StatutDocument }) => (
  <Badge variant="outline" className={cn('border-transparent', COULEUR_STATUT[statut])}>
    {LIBELLE_STATUT[statut]}
  </Badge>
);

export const EtapeBadge = ({ etape }: { etape: Etape | null }) => (etape ? <Badge variant="secondary">{LIBELLE_ETAPE[etape]}</Badge> : null);

const COULEUR_PAIEMENT: Record<EtatPaiement, string> = { A_PAYER: 'bg-amber-100 text-amber-900', LIQUIDE: 'bg-blue-100 text-blue-800', PAYE: 'bg-emerald-100 text-emerald-800' };
export const PaiementBadge = ({ etat }: { etat: EtatPaiement }) => (
  <Badge variant="outline" className={cn('border-transparent', COULEUR_PAIEMENT[etat])}>
    {LIBELLE_PAIEMENT[etat]}
  </Badge>
);

// ------------------------------------------------------------------ mise en page
export function PageHeader({ titre, sousTitre, actions, retour }: { titre: ReactNode; sousTitre?: ReactNode; actions?: ReactNode; retour?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {retour}
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{titre}</h1>
        {sousTitre && <div className="mt-1 text-sm text-muted-foreground">{sousTitre}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Kpi({ label, valeur, detail, accent }: { label: string; valeur: ReactNode; detail?: ReactNode; accent?: 'ok' | 'alerte' | 'critique' }) {
  return (
    <div className="@container rounded-lg border bg-card p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      {/* Valeur réduite dans les cartes étroites (requête de conteneur) : les montants et libellés longs restent lisibles sans coupure au milieu d’un mot. */}
      <div className={cn('num mt-1 break-words text-2xl font-semibold @max-2xs:text-xl @max-3xs:text-lg', accent === 'alerte' && 'text-amber-700', accent === 'critique' && 'text-red-700', accent === 'ok' && 'text-emerald-700')}>{valeur}</div>
      {detail && <div className="mt-1 text-xs text-muted-foreground">{detail}</div>}
    </div>
  );
}

/** Export Excel d'un rapport protégé : téléchargement avec la session, erreur en notification. */
export function BoutonExcel({ chemin, nom, libelle = 'Exporter (Excel)', taille = 'sm' }: { chemin: string; nom: string; libelle?: string; taille?: 'sm' | 'default' }) {
  const [enCours, setEnCours] = useState(false);
  const telecharger = async () => {
    setEnCours(true);
    try {
      await apiTelecharger(chemin, nom);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export impossible.');
    } finally {
      setEnCours(false);
    }
  };
  return (
    <Button type="button" variant={libelle ? 'outline' : 'ghost'} size={libelle ? taille : 'icon'} onClick={telecharger} disabled={enCours} title="Classeur Excel (.xlsx)" aria-label={libelle || 'Classeur Excel (.xlsx)'}>
      <FileSpreadsheet className="size-4" /> {libelle ? (enCours ? 'Préparation…' : libelle) : null}
    </Button>
  );
}

/** Document imprimable (PDF) ouvert dans un nouvel onglet avec la session. */
export function BoutonPdf({ chemin, nom, libelle = 'PDF', taille = 'default', variant = 'outline' }: { chemin: string; nom: string; libelle?: string; taille?: 'sm' | 'default' | 'icon'; variant?: 'outline' | 'ghost' }) {
  const [enCours, setEnCours] = useState(false);
  const ouvrir = async () => {
    setEnCours(true);
    try {
      await apiOuvrir(chemin, nom);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Document indisponible.');
    } finally {
      setEnCours(false);
    }
  };
  return (
    <Button type="button" variant={variant} size={taille} onClick={ouvrir} disabled={enCours} title="Version imprimable (PDF)" aria-label={taille === 'icon' ? 'Version imprimable (PDF)' : undefined}>
      <FileText className="size-4" /> {taille === 'icon' ? null : enCours ? 'Préparation…' : libelle}
    </Button>
  );
}

export const Chargement = () => (
  <div className="space-y-3">
    <Skeleton className="h-8 w-1/3" />
    <Skeleton className="h-24 w-full" />
    <Skeleton className="h-24 w-full" />
  </div>
);

export function ErreurBloc({ erreur }: { erreur: unknown }) {
  const message = erreur instanceof Error ? erreur.message : 'Erreur inattendue.';
  return (
    <Alert variant="destructive">
      <AlertTriangle className="size-4" />
      <AlertTitle>{erreur instanceof ApiError && erreur.status === 403 ? 'Accès refusé' : 'Erreur'}</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

export const Vide = ({ message }: { message: string }) => <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">{message}</div>;

// ------------------------------------------------------------------ circuit
const ICONE = {
  DONE: <CheckCircle2 className="size-5 text-emerald-600" />,
  CURRENT: <Clock className="size-5 text-blue-600" />,
  WAITING: <Circle className="size-5 text-muted-foreground/50" />,
  RETURNED_FOR_CORRECTION: <RotateCcw className="size-5 text-amber-600" />,
  REVALIDATION_REQUESTED: <RotateCcw className="size-5 text-violet-600" />,
};

export function CircuitTimeline({ circuit }: { circuit: Circuit | null }) {
  if (!circuit) return <Vide message="Le circuit sera construit à la soumission." />;
  return (
    <ol className="space-y-3">
      {circuit.steps.map((s, i) => (
        <li key={s.key} className="flex gap-3">
          <div className="flex flex-col items-center">
            {ICONE[s.status]}
            {i < circuit.steps.length - 1 && <div className="my-1 w-px flex-1 bg-border" />}
          </div>
          <div className="min-w-0 flex-1 pb-1">
            <div className={cn('text-sm font-medium', s.status === 'CURRENT' && 'text-blue-700')}>
              {LIBELLE_ETAPE[s.key]}
              {s.status === 'CURRENT' && <span className="ml-2 text-xs font-normal text-blue-700">étape en cours</span>}
            </div>
            {s.signedBy && (
              <div className="text-xs text-muted-foreground">
                {s.signedBy.name}
                {s.signedBy.onBehalfOfUserId ? ' (par intérim)' : ''} · {dateHeureFr(s.signedAt)}
              </div>
            )}
            {s.modifiedAfterValidation && <div className="mt-0.5 text-xs text-amber-700">Le dossier a changé après cette validation.</div>}
            {s.invalidated && <div className="mt-0.5 text-xs text-red-700">Invalidée à la suite de l'annulation du document.</div>}
            {s.status === 'RETURNED_FOR_CORRECTION' && <div className="text-xs text-amber-700">Correction demandée à cette étape.</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

const LIBELLE_KIND: Record<Circuit['history'][number]['kind'], string> = {
  SUBMIT: 'Soumission',
  VALIDATE: 'Validation',
  COMMENT: 'Commentaire',
  REQUEST_CORRECTION: 'Correction demandée',
  REJECT: 'Rejet',
  CANCEL: 'Annulation',
  RESUBMIT: 'Nouvelle soumission',
  REQUEST_REVALIDATION: 'Nouvelle validation demandée',
  EXPIRE: 'Annulation automatique (correction sans réponse)',
};

export function Historique({ circuit }: { circuit: Circuit | null }) {
  if (!circuit?.history.length) return null;
  return (
    <ul className="divide-y text-sm">
      {[...circuit.history].reverse().map((d, i) => (
        <li key={i} className="py-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium">
              {LIBELLE_KIND[d.kind]}
              {d.step ? ` · ${LIBELLE_ETAPE[d.step]}` : ''}
            </span>
            <span className="text-xs text-muted-foreground">{dateHeureFr(d.at)}</span>
          </div>
          <div className="text-xs text-muted-foreground">{d.actor.name}</div>
          {d.comment && <div className="mt-1 rounded bg-muted px-2 py-1 text-sm">{d.comment}</div>}
        </li>
      ))}
    </ul>
  );
}

export function ControlesListe({ controles }: { controles: Controle[] }) {
  if (!controles.length) return null;
  return (
    <ul className="space-y-1 text-sm">
      {controles.map((c, i) => (
        <li key={i} className="flex items-start gap-2">
          {c.ok ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 size-4 shrink-0 text-red-600" />}
          <span className={cn(!c.ok && 'text-red-700')}>{c.message}</span>
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------------ dialogues
const MOTIF_REQUIS: DecisionApi[] = ['COMMENTER', 'CORRIGER', 'REJETER', 'ANNULER'];

export function DecisionDialog({
  decisions,
  etape,
  onConfirmer,
  chargement,
  ouvert,
  onOpenChange,
}: {
  decisions: DecisionApi[];
  etape: Etape | null;
  onConfirmer: (decision: DecisionApi, motif?: string) => Promise<unknown>;
  chargement: boolean;
  ouvert: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [decision, setDecision] = useState<DecisionApi>('VALIDER');
  const [motif, setMotif] = useState('');
  const requis = MOTIF_REQUIS.includes(decision);
  const confirmer = async () => {
    await onConfirmer(decision, motif.trim() || undefined);
    setMotif('');
    setDecision('VALIDER');
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Décision · {etape ? LIBELLE_ETAPE[etape] : ''}</DialogTitle>
          <DialogDescription>Votre décision est signée électroniquement et inscrite au journal d'audit.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {decisions.map((d) => (
            <Button key={d} type="button" size="sm" variant={decision === d ? 'default' : 'outline'} onClick={() => setDecision(d)}>
              {LIBELLE_DECISION[d]}
            </Button>
          ))}
        </div>
        <div className="space-y-2">
          <Label htmlFor="motif">{requis ? 'Motif (obligatoire)' : 'Commentaire (facultatif)'}</Label>
          <Textarea id="motif" value={motif} onChange={(e) => setMotif(e.target.value)} rows={4} placeholder={decision === 'CORRIGER' ? "Indiquez précisément ce que l'émetteur doit corriger." : ''} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={confirmer} disabled={chargement || (requis && motif.trim().length === 0)} variant={decision === 'REJETER' || decision === 'ANNULER' ? 'destructive' : 'default'}>
            {LIBELLE_DECISION[decision]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmationDialog({
  titre,
  description,
  libelle,
  onConfirmer,
  chargement,
  ouvert,
  onOpenChange,
  champ,
}: {
  titre: string;
  description?: ReactNode;
  libelle: string;
  onConfirmer: (texte: string) => Promise<unknown>;
  chargement: boolean;
  ouvert: boolean;
  onOpenChange: (o: boolean) => void;
  champ?: { label: string; placeholder?: string; lignes?: number };
}) {
  const [texte, setTexte] = useState('');
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titre}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {champ && (
          <div className="space-y-2">
            <Label>{champ.label}</Label>
            <Textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={champ.lignes ?? 3} placeholder={champ.placeholder} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={() => onConfirmer(texte.trim())} disabled={chargement}>
            {libelle}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
